/**
 * Menu shown while an ``@`` mention is active. Work home portals it below
 * the composer; an in-chat composer still opens it above. Lists recent
 * sessions and patients (already filtered by the suggestion query); rows
 * are keyboard-navigable via the parent-owned ``activeIndex``.
 *
 * Selection uses ``onMouseDown`` (not ``onClick``) so picking a row
 * doesn't first blur the textarea — focus stays put and the hook can
 * restore the caret after stripping the ``@query`` token.
 */

import {
  menuLabelClasses,
  menuSeparatorClasses,
  menuSurfaceVariants,
} from '@heidi/design-system/styles';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { CalendarDays, FileOutput, UserRound, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, type JSX } from 'react';
import { useIntl } from 'react-intl';
import type { LinkedContextItem } from '../../../contracts/projects/linked-context';

export interface ContextMentionListProps {
  query: string;
  sessionItems: readonly LinkedContextItem[];
  patientItems: readonly LinkedContextItem[];
  libraryDocumentItems: readonly LinkedContextItem[];
  /** Active row in the flat [sessions…, patients…, library…] order. */
  activeIndex: number;
  /** Stable id for the listbox element (ARIA ``aria-controls`` target). */
  listboxId: string;
  /** Build the ``id`` for a row at the given flat index. */
  optionId: (index: number) => string;
  onHover: (index: number) => void;
  onSelect: (item: LinkedContextItem) => void;
  /**
   * The menu is positioned by a portal. Drops the upward `absolute` anchor,
   * which clips off the top of Work home.
   */
  anchored?: boolean;
  /** Scroll-region cap in px, from the viewport space beside the composer. */
  listMaxHeight?: number;
}

export const ContextMentionList = ({
  query,
  sessionItems,
  patientItems,
  libraryDocumentItems,
  activeIndex,
  listboxId,
  optionId,
  onHover,
  onSelect,
  anchored = false,
  listMaxHeight,
}: ContextMentionListProps): JSX.Element => {
  const intl = useIntl();
  const isEmpty =
    sessionItems.length === 0 &&
    patientItems.length === 0 &&
    libraryDocumentItems.length === 0;
  const heading = intl.formatMessage({
    defaultMessage: 'Link a session or patient',
    id: 'Az0vz95Y7u',
  });

  return (
    <div
      id={listboxId}
      role="listbox"
      aria-label={heading}
      data-testid="context-mention-autocomplete"
      className={cn(
        menuSurfaceVariants(),
        'overflow-hidden',
        anchored ? 'w-full' : 'absolute bottom-full start-0 mb-2 w-72'
      )}
    >
      <TypographyCaption className="px-2 py-1.5 text-text-secondary">
        {heading}
        {query ? (
          <span className="ms-1 text-text-primary">
            {intl.formatMessage(
              { defaultMessage: '"{query}"', id: 'JvUq3cgdxX' },
              { query }
            )}
          </span>
        ) : null}
      </TypographyCaption>
      <div aria-hidden className={menuSeparatorClasses} />

      {isEmpty ? (
        <TypographyCaption className="px-2 py-1.5 text-text-tertiary">
          {intl.formatMessage({ defaultMessage: 'No matches', id: '96GJ5wSQam' })}
        </TypographyCaption>
      ) : (
        <div
          className={cn(
            listMaxHeight == null && 'max-h-64',
            'overflow-y-auto scrollbar-none'
          )}
          style={listMaxHeight == null ? undefined : { maxHeight: listMaxHeight }}
        >
          {sessionItems.length > 0 && (
            <MentionSection
              heading={intl.formatMessage({
                defaultMessage: 'Recent sessions',
                id: 'y2klVeZo7g',
              })}
            >
              {sessionItems.map((item, i) => {
                if (item.type !== 'session') return null;
                return (
                  <MentionRow
                    key={`session:${item.sessionId}`}
                    id={optionId(i)}
                    item={item}
                    index={i}
                    isActive={activeIndex === i}
                    onHover={onHover}
                    onSelect={onSelect}
                    icon={CalendarDays}
                    label={item.sessionName}
                    sub={item.patientName}
                  />
                );
              })}
            </MentionSection>
          )}
          {patientItems.length > 0 && (
            <MentionSection
              heading={intl.formatMessage({
                defaultMessage: 'Recent patients',
                id: 'NsPq+mAXT4',
              })}
            >
              {patientItems.map((item, j) => {
                if (item.type !== 'patient') return null;
                const index = sessionItems.length + j;
                return (
                  <MentionRow
                    key={`patient:${item.patientName.toLowerCase()}`}
                    id={optionId(index)}
                    item={item}
                    index={index}
                    isActive={activeIndex === index}
                    onHover={onHover}
                    onSelect={onSelect}
                    icon={UserRound}
                    label={item.patientName}
                  />
                );
              })}
            </MentionSection>
          )}
          {libraryDocumentItems.length > 0 && (
            <MentionSection
              heading={intl.formatMessage({
                defaultMessage: 'Artifacts',
                id: 'eW5eoWkxy3',
              })}
            >
              {libraryDocumentItems.map((item, k) => {
                const index = sessionItems.length + patientItems.length + k;
                const doc = item.type === 'library_document' ? item : null;
                return (
                  <MentionRow
                    key={`library:${doc?.documentId ?? k}`}
                    id={optionId(index)}
                    item={item}
                    index={index}
                    isActive={activeIndex === index}
                    onHover={onHover}
                    onSelect={onSelect}
                    icon={FileOutput}
                    label={doc?.filename ?? 'Document'}
                    sub={
                      doc?.documentKind === 'workflow_context'
                        ? intl.formatMessage({
                            defaultMessage: 'Skill file',
                            id: 'yKx3EoNw7q',
                          })
                        : doc?.documentKind === 'input'
                          ? intl.formatMessage({
                              defaultMessage: 'Upload',
                              id: 'z/FkCzeO6O',
                              description:
                                'Composer mention subtitle for an uploaded Library document',
                            })
                          : intl.formatMessage({
                              defaultMessage: 'Heidi output',
                              id: 'V/3w48ikt1',
                            })
                    }
                  />
                );
              })}
            </MentionSection>
          )}
        </div>
      )}
    </div>
  );
};

interface MentionSectionProps {
  heading: string;
  children: React.ReactNode;
}

const MentionSection = ({ heading, children }: MentionSectionProps): JSX.Element => (
  <section className="flex flex-col">
    <span className={menuLabelClasses}>{heading}</span>
    {children}
  </section>
);

interface MentionRowProps {
  id: string;
  item: LinkedContextItem;
  index: number;
  isActive: boolean;
  onHover: (index: number) => void;
  onSelect: (item: LinkedContextItem) => void;
  icon: LucideIcon;
  label: string;
  sub?: string;
}

const MentionRow = ({
  id,
  item,
  index,
  isActive,
  onHover,
  onSelect,
  icon: Icon,
  label,
  sub,
}: MentionRowProps): JSX.Element => {
  const rowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // `nearest` only moves the list when the row is outside it, so arrowing
    // through a long menu follows the highlight without jumping a visible row.
    if (isActive) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isActive]);

  return (
    <button
      ref={rowRef}
      type="button"
      id={id}
      role="option"
      aria-selected={isActive}
      onMouseEnter={() => onHover(index)}
      // Mouse-down (pre-blur) so the textarea keeps focus through select.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(item);
      }}
      // No `hover:` highlight: `onMouseEnter` already moves the active row, and a
      // CSS hover would keep a second row lit while the keyboard moves away.
      className={cn(
        'flex w-full cursor-pointer select-none flex-col gap-0.5 rounded-md px-2 py-1.5 text-start text-text-primary outline-none',
        isActive && 'bg-surface-hover'
      )}
    >
      {/* Icon shares the title's line and the description indents past it
          (`ps-6` = icon + gap), as DropdownMenuItem does. */}
      <span className="flex w-full items-center gap-2">
        <Icon className="size-4 shrink-0 text-text-tertiary" aria-hidden />
        <span className={cn(typographyVariant({ variant: 'p2' }), 'min-w-0 truncate')}>
          {label}
        </span>
      </span>
      {sub && (
        <span
          className={cn(
            typographyVariant({ variant: 'caption' }),
            'w-full truncate ps-6',
            isActive ? 'text-text-secondary' : 'text-text-tertiary'
          )}
        >
          {sub}
        </span>
      )}
    </button>
  );
};
